(() => {
  'use strict';

  const root = document.documentElement;
  const body = document.body;
  const liveRegion = document.querySelector('[data-live-region]');
  const overlay = document.querySelector('[data-drawer-overlay]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let activeDrawer = null;
  let lastTrigger = null;

  const announce = (message) => {
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(() => { liveRegion.textContent = message; }, 50);
  };

  /* ---------- Motion primitives ----------
     Springs described the way Apple does: `response` (seconds, how quickly it gets there) and
     `damping` (1 = no overshoot). They always start from the value on screen and keep the
     current velocity when retargeted, so every animation can be grabbed and reversed. */
  const motionOK = () => !reduceMotion.matches;
  if (motionOK()) root.classList.add('js-motion');
  reduceMotion.addEventListener?.('change', () => root.classList.toggle('js-motion', motionOK()));

  const Spring = ({ value = 0, response = 0.4, damping = 1, precision = 1, onUpdate = () => {}, onRest = () => {} } = {}) => {
    let x = value;
    let v = 0;
    let target = value;
    let k = 0;
    let c = 0;
    let raf = 0;
    let last = 0;
    const configure = (r, d) => { k = Math.pow((2 * Math.PI) / r, 2); c = (4 * Math.PI * d) / r; };
    configure(response, damping);
    const tick = (now) => {
      const dt = Math.min(0.064, (now - last) / 1000);
      last = now;
      const steps = Math.max(1, Math.ceil(dt / 0.004));
      const h = dt / steps;
      for (let i = 0; i < steps; i += 1) {
        v += (-k * (x - target) - c * v) * h;
        x += v * h;
      }
      // precision scales the rest thresholds: 1 suits pixels, ~0.005 suits scale factors
      if (Math.abs(v) < 2 * precision && Math.abs(x - target) < 0.4 * precision) {
        x = target; v = 0; raf = 0;
        onUpdate(x);
        onRest(x);
        return;
      }
      onUpdate(x);
      raf = requestAnimationFrame(tick);
    };
    return {
      get value() { return x; },
      get target() { return target; },
      get moving() { return raf !== 0; },
      to(next, options = {}) {
        target = next;
        if (typeof options.velocity === 'number') v = options.velocity;
        configure(options.response || response, options.damping || damping);
        if (!motionOK()) { this.set(next); onRest(next); return; }
        if (!raf) { last = performance.now(); raf = requestAnimationFrame(tick); }
      },
      set(next) { this.stop(); x = next; target = next; v = 0; onUpdate(x); },
      stop() { if (raf) cancelAnimationFrame(raf); raf = 0; }
    };
  };

  // Last ~100ms of positions, so release velocity reflects the flick rather than the whole drag
  const VelocityTracker = () => {
    let points = [];
    return {
      add(value, time) {
        points.push([value, time]);
        while (points.length > 2 && time - points[0][1] > 100) points.shift();
      },
      velocity() {
        if (points.length < 2) return 0;
        const [x0, t0] = points[0];
        const [x1, t1] = points[points.length - 1];
        const dt = (t1 - t0) / 1000;
        return dt > 0 ? (x1 - x0) / dt : 0;
      },
      reset() { points = []; }
    };
  };

  // Where a flick would come to rest (Apple's projection from "Designing Fluid Interfaces")
  const project = (velocity, rate = 0.998) => ((velocity / 1000) * rate) / (1 - rate);
  // Soft edge: the further past the bound, the less it follows
  const rubberband = (overshoot, dimension, constant = 0.55) => (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot));
  const DRAG_SLOP = 10;

  const suppressNextClick = (element) => {
    const block = (event) => { event.preventDefault(); event.stopPropagation(); };
    element.addEventListener('click', block, { capture: true, once: true });
    window.setTimeout(() => element.removeEventListener('click', block, { capture: true }), 400);
  };

  /* ---------- Drawers ----------
     The sticky header creates its own stacking context, so drawers that live inside it
     are moved to <body> to sit above the page overlay. Each drawer is a spring: it follows
     the finger 1:1, rubber-bands past open, and is thrown shut or back open by its momentum.
     The dismiss direction comes from CSS (--dismiss), so the cart can be a side panel on
     desktop and a bottom sheet on phones. */
  const hoistDrawers = () => {
    document.querySelectorAll('.shopify-section .drawer[id]').forEach((drawer) => {
      const stale = Array.from(document.querySelectorAll(`body > #${drawer.id}`)).filter((el) => el !== drawer);
      stale.forEach((el) => el.remove());
      body.appendChild(drawer);
    });
  };
  hoistDrawers();

  const focusable = (container) => Array.from(container.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'));

  const controllers = new WeakMap();
  const setOverlay = (progress) => {
    if (!overlay) return;
    overlay.style.opacity = String(Math.max(0, Math.min(1, progress)));
  };

  const controllerFor = (drawer) => {
    if (controllers.has(drawer)) return controllers.get(drawer);
    const ctl = { open: false, dir: 'right', size: 1 };
    const horizontal = () => ctl.dir === 'left' || ctl.dir === 'right';
    ctl.measure = () => {
      ctl.dir = getComputedStyle(drawer).getPropertyValue('--dismiss').trim() || 'right';
      const rect = drawer.getBoundingClientRect();
      ctl.size = Math.max(1, (horizontal() ? rect.width : rect.height) + 32);
    };
    const render = (x) => {
      const sign = ctl.dir === 'right' || ctl.dir === 'down' ? 1 : -1;
      drawer.style.transform = horizontal() ? `translate3d(${(x * sign).toFixed(2)}px,0,0)` : `translate3d(0,${(x * sign).toFixed(2)}px,0)`;
      if (activeDrawer === drawer || !activeDrawer) setOverlay(1 - x / ctl.size);
    };
    ctl.spring = Spring({
      value: 0,
      response: 0.38,
      onUpdate: render,
      onRest: (x) => { if (!ctl.open && x >= ctl.size - 1) finishClose(drawer); }
    });
    ctl.measure();
    controllers.set(drawer, ctl);
    bindDrawerDrag(drawer, ctl);
    return ctl;
  };

  const finishClose = (drawer) => {
    drawer.classList.remove('is-open');
    drawer.style.transform = '';
    if (!activeDrawer) {
      body.classList.remove('drawer-open');
      if (overlay) { overlay.hidden = true; overlay.style.opacity = ''; overlay.style.pointerEvents = ''; }
    }
  };

  // `velocity` is passed when a drag hands off to the spring: keep the current position and carry the speed
  const openDrawer = (drawer, trigger, velocity = null) => {
    if (!drawer) return;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(activeDrawer, false);
    const ctl = controllerFor(drawer);
    if (ctl.open && activeDrawer === drawer) return;
    const wasMoving = ctl.spring.moving;
    activeDrawer = drawer;
    lastTrigger = trigger || document.activeElement;
    ctl.open = true;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    body.classList.add('drawer-open');
    if (overlay) { overlay.hidden = false; overlay.style.pointerEvents = ''; }
    document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'true'));
    // Interrupted mid-close? Reverse from where it is. Otherwise start fully off-screen.
    if (velocity === null && !wasMoving) { ctl.measure(); ctl.spring.set(ctl.size); }
    if (velocity === null) ctl.spring.to(0, { response: 0.44, damping: 1 });
    else ctl.spring.to(0, { velocity, response: 0.36, damping: 1 });
    const preferred = drawer.querySelector('input[type="search"]') || focusable(drawer)[0];
    if (preferred && velocity === null) window.setTimeout(() => preferred.focus({ preventScroll: true }), 80);
  };

  const closeDrawer = (drawer = activeDrawer, restoreFocus = true, velocity = 0) => {
    if (!drawer) return;
    const ctl = controllerFor(drawer);
    ctl.open = false;
    drawer.setAttribute('aria-hidden', 'true');
    document.querySelectorAll(`[aria-controls="${drawer.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'false'));
    if (activeDrawer === drawer) activeDrawer = null;
    // Never lock out input while it animates away: taps go straight through to the page
    if (!activeDrawer && overlay) overlay.style.pointerEvents = 'none';
    ctl.spring.to(ctl.size, { velocity, response: 0.36, damping: 1 });
    if (restoreFocus && lastTrigger) lastTrigger.focus({ preventScroll: true });
  };

  function bindDrawerDrag(drawer, ctl) {
    const tracker = VelocityTracker();
    let pointerId = null;
    let start = null;
    let startValue = 0;
    let dragging = false;
    let caught = false;

    const along = (event) => {
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      const map = { right: [dx, dy], left: [-dx, dy], down: [dy, dx], up: [-dy, dx] };
      return map[ctl.dir] || map.right;
    };

    drawer.addEventListener('pointerdown', (event) => {
      // Open, or still sliding away: a closing drawer can be caught and pulled back
      if ((!ctl.open && !ctl.spring.moving) || pointerId !== null) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('input, select, textarea, button, a, summary, [data-no-drag]')) return;
      ctl.measure();
      // A bottom sheet's list scrolls natively, so it is only dragged by its grabber and header
      if (!['left', 'right'].includes(ctl.dir) && event.target.closest('.drawer__body')) return;
      pointerId = event.pointerId;
      start = { x: event.clientX, y: event.clientY };
      startValue = ctl.spring.value;
      dragging = false;
      tracker.reset();
      // Touching a drawer in motion stops it under the finger, like catching a moving card
      caught = ctl.spring.moving;
      if (caught) { ctl.spring.stop(); drawer.setPointerCapture(event.pointerId); }
    });

    drawer.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const [d, cross] = along(event);
      if (!dragging) {
        if (Math.max(Math.abs(d), Math.abs(cross)) < DRAG_SLOP) return;
        if (Math.abs(d) <= Math.abs(cross)) {
          pointerId = null;
          if (caught) { caught = false; ctl.spring.to(ctl.open ? 0 : ctl.size); }
          return;
        }
        dragging = true;
        if (!caught) drawer.setPointerCapture(event.pointerId);
        ctl.spring.stop();
        startValue = ctl.spring.value - d; // continue from where it is: no jump when the drag engages
      }
      let x = startValue + d;
      if (x < 0) x = -rubberband(-x, ctl.size);
      ctl.spring.set(x);
      tracker.add(x, event.timeStamp);
      event.preventDefault();
    });

    const release = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      if (!dragging) {
        // Caught but not moved: let it carry on where it was going
        if (caught) ctl.spring.to(ctl.open ? 0 : ctl.size);
        caught = false;
        return;
      }
      dragging = false;
      caught = false;
      suppressNextClick(drawer);
      const x = ctl.spring.value;
      const velocity = tracker.velocity();
      const resting = x + project(velocity);
      if (resting > ctl.size * 0.5) closeDrawer(drawer, true, velocity);
      // A flick back open earns a touch of bounce; a slow release settles without one
      else if (!ctl.open) openDrawer(drawer, lastTrigger, velocity);
      else ctl.spring.to(0, { velocity, response: 0.36, damping: 1 });
    };
    drawer.addEventListener('pointerup', release);
    drawer.addEventListener('pointercancel', release);
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      event.preventDefault();
      openDrawer(document.querySelector(opener.dataset.drawerOpen), opener);
      return;
    }
    if (event.target.closest('[data-drawer-close]')) closeDrawer();
  });
  overlay?.addEventListener('click', () => closeDrawer());
  document.addEventListener('shopify:section:load', hoistDrawers);
  window.addEventListener('resize', () => {
    if (!activeDrawer) return;
    const ctl = controllerFor(activeDrawer);
    ctl.measure();
    ctl.spring.set(0);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeDrawer) closeDrawer();
    if (event.key !== 'Tab' || !activeDrawer) return;
    const items = focusable(activeDrawer);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  /* ---------- Product gallery: a strip of slides on a spring ----------
     Tracks the finger 1:1, rubber-bands at the first and last image, and uses the flick's
     projected landing point to pick the next slide (one at a time, like paging on iOS). */
  document.querySelectorAll('[data-gallery-stage]').forEach((stage) => {
    const track = stage.querySelector('[data-gallery-track]');
    if (!track) return;
    const slides = Array.from(track.children);
    if (slides.length < 2) return;
    let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let width = stage.clientWidth || 1;
    const spring = Spring({ response: 0.42, onUpdate: (x) => { track.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0)`; } });
    const clamp = (i) => Math.max(0, Math.min(slides.length - 1, i));
    const warm = (i) => [i - 1, i, i + 1].forEach((n) => slides[n]?.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; }));
    const go = (next, velocity = 0, announceChange = false) => {
      index = clamp(next);
      warm(index);
      spring.to(-index * width, { velocity, damping: Math.abs(velocity) > 400 ? 0.86 : 1 });
      if (announceChange) stage.dispatchEvent(new CustomEvent('sm:gallery-settle', { detail: { index } }));
    };
    spring.set(-index * width);
    warm(index);
    stage.classList.add('is-sliding');
    stage.addEventListener('sm:gallery-go', (event) => go(event.detail.index));
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => { width = stage.clientWidth || 1; spring.set(-index * width); }).observe(stage);
    }

    const tracker = VelocityTracker();
    let pointerId = null;
    let start = null;
    let startX = 0;
    let startIndex = 0;
    let dragging = false;
    stage.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerId = event.pointerId;
      start = { x: event.clientX, y: event.clientY };
      startX = spring.value;
      startIndex = index;
      dragging = false;
      tracker.reset();
    });
    stage.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (!dragging) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < DRAG_SLOP) return;
        if (Math.abs(dx) <= Math.abs(dy)) { pointerId = null; return; }
        dragging = true;
        stage.setPointerCapture(event.pointerId);
        spring.stop();
        startX = spring.value - dx;
      }
      const min = -(slides.length - 1) * width;
      let x = startX + dx;
      if (x > 0) x = rubberband(x, width);
      else if (x < min) x = min - rubberband(min - x, width);
      spring.set(x);
      tracker.add(x, event.timeStamp);
      event.preventDefault();
    });
    const release = (event) => {
      if (event.pointerId !== pointerId) return;
      pointerId = null;
      if (!dragging) return;
      dragging = false;
      suppressNextClick(stage);
      const velocity = tracker.velocity();
      const resting = spring.value + project(velocity, 0.99);
      const nearest = Math.round(-resting / width);
      go(Math.max(startIndex - 1, Math.min(startIndex + 1, nearest)), velocity, true);
    };
    stage.addEventListener('pointerup', release);
    stage.addEventListener('pointercancel', release);
    stage.addEventListener('dragstart', (event) => event.preventDefault());
    stage.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      go(index + (event.key === 'ArrowRight' ? 1 : -1), 0, true);
    });
  });

  /* ---------- Header: hairline once the page moves ---------- */
  const header = document.querySelector('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Announcement: one message at a time ---------- */
  document.querySelectorAll('[data-announcement]').forEach((bar) => {
    const messages = Array.from(bar.querySelectorAll('[data-announcement-message]'));
    if (messages.length < 2 || reduceMotion.matches) return;
    let index = 0;
    let paused = false;
    bar.addEventListener('mouseenter', () => { paused = true; });
    bar.addEventListener('mouseleave', () => { paused = false; });
    window.setInterval(() => {
      if (paused || document.hidden) return;
      const current = messages[index];
      index = (index + 1) % messages.length;
      const next = messages[index];
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');
      next.classList.remove('is-leaving');
      next.classList.add('is-active');
      next.removeAttribute('aria-hidden');
      window.setTimeout(() => current.classList.remove('is-leaving'), 800);
    }, Number(bar.dataset.speed || 5) * 1000);
  });

  /* ---------- Reveal ---------- */
  const revealAll = (scope = document) => scope.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => el.classList.add('is-visible'));
  let revealObserver = null;
  if ('IntersectionObserver' in window && !reduceMotion.matches && !root.classList.contains('no-reveal')) {
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.04, rootMargin: '0px 0px 24px 0px' });
  }
  const observeReveals = (scope = document) => {
    if (!revealObserver) { revealAll(scope); return; }
    scope.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => revealObserver.observe(el));
  };
  observeReveals();

  /* ---------- Scroll clock ----------
     One rAF-throttled scroll/resize loop that every scroll effect subscribes to,
     so the page does a single read/write pass per frame. */
  const scrollEffects = [];
  let scrollQueued = false;
  const runScrollEffects = () => {
    scrollQueued = false;
    const vh = window.innerHeight;
    scrollEffects.forEach((effect) => effect(vh));
  };
  const queueScroll = () => { if (!scrollQueued) { scrollQueued = true; requestAnimationFrame(runScrollEffects); } };
  window.addEventListener('scroll', queueScroll, { passive: true });
  window.addEventListener('resize', queueScroll);
  window.addEventListener('load', queueScroll);
  const onScrollFrame = (effect) => { scrollEffects.push(effect); queueScroll(); };
  const clamp01 = (n) => Math.max(0, Math.min(1, n));
  const lerp = (a, b, t) => a + (b - a) * t;
  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  /* ---------- Hero: the arch opens into the store ----------
     The hero is pinned while you scroll. The words lift away and blur, and the arch grows
     from a framed window into the whole screen, its curved top flattening as it goes.
     Everything is driven by scroll position, so it moves exactly as fast as your thumb. */
  document.querySelectorAll('[data-hero]').forEach((hero) => {
    requestAnimationFrame(() => requestAnimationFrame(() => hero.classList.add('is-loaded')));
    if (!hero.classList.contains('sm-hero--pin') || !motionOK()) { hero.classList.remove('sm-hero--pin'); return; }
    const sticky = hero.querySelector('[data-hero-sticky]');
    const arch = hero.querySelector('[data-hero-arch]');
    const media = hero.querySelector('[data-hero-media]');
    const copy = hero.querySelector('[data-hero-copy]');
    const line = hero.querySelector('[data-hero-line]');
    const swan = hero.querySelector('[data-hero-swan]');
    const reveal = hero.querySelector('[data-hero-reveal]');
    if (!sticky || !arch) return;
    let base = null;
    const measure = () => {
      arch.style.transform = '';
      const r = arch.getBoundingClientRect();
      const s = sticky.getBoundingClientRect();
      base = { w: r.width, h: r.height, cx: r.left + r.width / 2 - s.left, cy: r.top + r.height / 2 - s.top, sw: s.width, sh: s.height };
    };
    window.addEventListener('resize', () => { base = null; });
    onScrollFrame(() => {
      const rect = hero.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      if (!hero.classList.contains('is-settled')) return; // let the entrance finish first
      if (!base) measure();
      const travel = Math.max(1, hero.offsetHeight - sticky.offsetHeight);
      const p = clamp01(-rect.top / (travel * 0.82));
      const e = easeInOut(p);
      // Grow the arch until it covers the stage, moving its centre to the stage centre
      const cover = Math.max(base.sw / base.w, base.sh / base.h) * 1.01;
      const scale = lerp(1, cover, e);
      const dx = lerp(0, base.sw / 2 - base.cx, e);
      const dy = lerp(0, base.sh / 2 - base.cy, e);
      arch.style.transform = `translate3d(${dx.toFixed(1)}px, ${dy.toFixed(1)}px, 0) scale(${scale.toFixed(4)})`;
      // Radius is set in unscaled pixels, so divide by scale to flatten smoothly on screen
      const top = (base.w / 2) * (1 - e) / scale;
      const bottom = 18 * (1 - e) / scale;
      arch.style.borderRadius = `${top.toFixed(1)}px ${top.toFixed(1)}px ${bottom.toFixed(1)}px ${bottom.toFixed(1)}px`;
      if (media) media.style.transform = `translate3d(0, ${(-4 * e).toFixed(2)}%, 0) scale(${lerp(1.08, 1, e).toFixed(4)})`;
      if (copy) {
        const c = clamp01(p / 0.55);
        copy.style.transform = `translate3d(0, ${(-70 * c).toFixed(1)}px, 0) scale(${lerp(1, 0.96, c).toFixed(4)})`;
        copy.style.opacity = (1 - c).toFixed(3);
        copy.style.filter = c > 0.01 ? `blur(${(8 * c).toFixed(1)}px)` : '';
        copy.style.visibility = c >= 1 ? 'hidden' : '';
      }
      if (line) line.style.opacity = (1 - clamp01(p / 0.3)).toFixed(3);
      if (swan) swan.style.transform = `translate3d(0, ${(-60 * p).toFixed(1)}px, 0) rotate(${(-4 * p).toFixed(2)}deg)`;
      if (reveal) {
        const t = clamp01((p - 0.72) / 0.28);
        reveal.style.opacity = t.toFixed(3);
        reveal.style.transform = `translate3d(-50%, calc(-50% + ${(24 * (1 - t)).toFixed(1)}px), 0) scale(${lerp(0.94, 1, t).toFixed(4)})`;
        reveal.style.filter = t < 0.99 ? `blur(${(10 * (1 - t)).toFixed(1)}px)` : '';
      }
    });
    // Entrance uses CSS transitions; scroll choreography takes over once they finish
    window.setTimeout(() => { hero.classList.add('is-settled'); base = null; queueScroll(); }, 1700);
  });

  /* ---------- Headings rise word by word out of a mask ---------- */
  const splitHeading = (heading) => {
    if (heading.dataset.split || heading.querySelector('*:not(br)')) return;
    const text = heading.textContent.trim();
    if (!text) return;
    heading.dataset.split = 'true';
    heading.setAttribute('aria-label', text);
    heading.innerHTML = text.split(/\s+/).map((word, i) => `<span class="sm-word" aria-hidden="true"><span style="--w:${i}">${word.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch])}</span></span>`).join(' ');
    heading.classList.add('sm-rise');
  };
  if (motionOK() && 'IntersectionObserver' in window) {
    const riseObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-risen');
        riseObserver.unobserve(entry.target);
      });
    }, { threshold: 0.3, rootMargin: '0px 0px -8% 0px' });
    document.querySelectorAll('.section-header h2, .sm-categories__head h2, .sm-story__content h2, .sm-newsletter h2, .sm-collection__hero h1, .page-hero h1, .product-info h1, .sm-page-story__hero h1').forEach((heading) => {
      splitHeading(heading);
      if (heading.classList.contains('sm-rise')) riseObserver.observe(heading);
    });
  }

  /* ---------- Story band: the image settles as it slides into view ---------- */
  if (motionOK()) {
    document.querySelectorAll('.sm-story').forEach((band) => {
      const img = band.querySelector('.sm-story__media img, .sm-story__media video');
      const content = band.querySelector('.sm-story__content');
      if (!img) return;
      onScrollFrame((vh) => {
        const r = band.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        const p = clamp01((vh - r.top) / (vh + r.height));
        img.style.transform = `translate3d(0, ${lerp(-6, 6, p).toFixed(2)}%, 0) scale(${lerp(1.18, 1.06, clamp01(p * 1.6)).toFixed(4)})`;
        if (content) content.style.transform = `translate3d(0, ${lerp(40, -20, p).toFixed(1)}px, 0)`;
      });
    });
  }

  /* ---------- Header: steps aside while you read, returns the moment you scroll up ---------- */
  if (header && motionOK()) {
    const section = header.closest('.sm-header-section');
    if (section && !section.classList.contains('is-static')) {
      let lastY = window.scrollY;
      let hidden = false;
      const offset = Spring({ value: 0, response: 0.42, onUpdate: (y) => { section.style.transform = y ? `translate3d(0, ${y.toFixed(2)}px, 0)` : ''; } });
      onScrollFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY;
        lastY = y;
        if (body.classList.contains('drawer-open')) return;
        const shouldHide = y > 420 && delta > 6;
        const shouldShow = delta < -6 || y < 200;
        if (shouldHide && !hidden) { hidden = true; offset.to(-(section.offsetHeight + 8)); }
        else if (shouldShow && hidden) { hidden = false; offset.to(0, { velocity: Math.min(1600, -delta * 40) }); }
      });
      document.addEventListener('focusin', (event) => { if (hidden && section.contains(event.target)) { hidden = false; offset.to(0); } });
    }
  }

  /* ---------- Cards tilt toward the pointer, with light moving across them ----------
     Two springs per card (X and Y kept independent), so the tilt eases in, follows,
     and settles back when the pointer leaves. Mouse and trackpad only. */
  if (motionOK() && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    const tiltable = '.sm-card__media, .sm-category-card';
    const bindTilt = (el) => {
      if (el.dataset.tilt) return;
      el.dataset.tilt = 'true';
      const max = 7;
      const render = () => {
        el.style.setProperty('--rx', `${rx.value.toFixed(3)}deg`);
        el.style.setProperty('--ry', `${ry.value.toFixed(3)}deg`);
      };
      // A card's full-size link sits above its photo, so listen on the whole card
      const target = el.closest('.sm-card') || el;
      const rx = Spring({ response: 0.5, damping: 0.86, precision: 0.05, onUpdate: render });
      const ry = Spring({ response: 0.5, damping: 0.86, precision: 0.05, onUpdate: render });
      target.addEventListener('pointermove', (event) => {
        if (event.pointerType !== 'mouse') return;
        const r = el.getBoundingClientRect();
        const px = (event.clientX - r.left) / r.width;
        const py = (event.clientY - r.top) / r.height;
        rx.to((0.5 - py) * max);
        ry.to((px - 0.5) * max);
        el.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`);
        el.style.setProperty('--my', `${(py * 100).toFixed(1)}%`);
        el.classList.add('is-tilting');
      });
      target.addEventListener('pointerleave', () => { rx.to(0); ry.to(0); el.classList.remove('is-tilting'); });
    };
    document.querySelectorAll(tiltable).forEach(bindTilt);
    new MutationObserver(() => document.querySelectorAll(tiltable).forEach(bindTilt)).observe(document.body, { childList: true, subtree: true });
  }

  /* ---------- Category rail: drag it with a mouse, throw it, and it lands on a card ---------- */
  document.querySelectorAll('[data-rail]').forEach((rail) => {
    if (!motionOK()) return;
    const tracker = VelocityTracker();
    let down = null;
    let dragging = false;
    const spring = Spring({ response: 0.5, onUpdate: (x) => { rail.scrollLeft = x; }, onRest: () => { rail.style.scrollSnapType = ''; rail.style.scrollBehavior = ''; } });
    const snapPoints = () => Array.from(rail.children).map((child) => child.offsetLeft - rail.firstElementChild.offsetLeft);
    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      spring.stop();
      down = { x: event.clientX, left: rail.scrollLeft };
      dragging = false;
      tracker.reset();
    });
    rail.addEventListener('pointermove', (event) => {
      if (!down) return;
      const dx = event.clientX - down.x;
      if (!dragging) {
        if (Math.abs(dx) < DRAG_SLOP) return;
        dragging = true;
        rail.setPointerCapture(event.pointerId);
        rail.style.scrollSnapType = 'none';
        rail.style.scrollBehavior = 'auto';
        rail.classList.add('is-dragging');
      }
      const max = rail.scrollWidth - rail.clientWidth;
      let left = down.left - dx;
      if (left < 0) left = -rubberband(-left, rail.clientWidth);
      else if (left > max) left = max + rubberband(left - max, rail.clientWidth);
      rail.scrollLeft = left;
      tracker.add(-event.clientX, event.timeStamp);
    });
    const end = () => {
      if (!down) return;
      down = null;
      rail.classList.remove('is-dragging');
      if (!dragging) return;
      dragging = false;
      suppressNextClick(rail);
      const velocity = tracker.velocity();
      const max = rail.scrollWidth - rail.clientWidth;
      const resting = rail.scrollLeft + project(velocity);
      const points = snapPoints().filter((x) => x <= max + 1).concat(max);
      const target = points.reduce((best, x) => (Math.abs(x - resting) < Math.abs(best - resting) ? x : best), points[0]);
      spring.set(rail.scrollLeft);
      spring.to(Math.max(0, Math.min(max, target)), { velocity, damping: Math.abs(velocity) > 600 ? 0.9 : 1 });
    };
    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
    rail.addEventListener('dragstart', (event) => event.preventDefault());
  });

  /* ---------- Page transitions: the product photo travels with you ----------
     Cross-document View Transitions (Chrome, Edge, Safari 18.2+). The tapped card's photo
     is named so it morphs into the product page gallery; other browsers simply navigate. */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.sm-card a[href]');
    if (!link || !motionOK() || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const destination = new URL(link.href, location.href);
    if (destination.origin !== location.origin || !destination.pathname.includes('/products/')) return;
    document.querySelectorAll('.product-gallery__stage').forEach(el => { el.style.viewTransitionName = 'none'; });
    document.querySelectorAll('.sm-card__media').forEach((el) => { el.style.viewTransitionName = ''; });
    const media = link.closest('.sm-card')?.querySelector('.sm-card__media');
    if (media) media.style.viewTransitionName = 'sm-product-media';
  });
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.product-gallery__stage').forEach(el => { el.style.viewTransitionName = ''; });
    document.querySelectorAll('.sm-card__media').forEach((el) => { el.style.viewTransitionName = ''; });
  });

  /* ---------- Added to bag: the photo arcs into the bag icon ----------
     X and Y are separate springs with different responses, which draws the curve. */
  const flyToBag = (form) => new Promise((resolve) => {
    const bag = Array.from(document.querySelectorAll('[data-cart-trigger]')).find((el) => el.getBoundingClientRect().width > 0);
    const source = form.closest('[data-look-card]')?.querySelector('img') || form.closest('[id^="SMProduct"], .sm-pdp')?.querySelector('.product-gallery__item.is-active img') || form.closest('.sm-card')?.querySelector('img') || document.querySelector('.product-gallery__item.is-active img');
    if (!motionOK() || form.closest('#CartDrawer') || !bag || !source) { resolve(); return; }
    const from = source.getBoundingClientRect();
    const to = bag.getBoundingClientRect();
    if (from.bottom < 0 || from.top > window.innerHeight) { resolve(); return; }
    const size = Math.min(from.width, from.height, 96);
    const ghost = document.createElement('img');
    ghost.src = source.currentSrc || source.src;
    ghost.alt = '';
    ghost.className = 'sm-fly';
    ghost.style.width = ghost.style.height = `${size}px`;
    document.body.appendChild(ghost);
    const startX = from.left + from.width / 2 - size / 2;
    const startY = from.top + from.height / 2 - size / 2;
    const endX = to.left + to.width / 2 - size / 2;
    const endY = to.top + to.height / 2 - size / 2;
    let x = startX;
    let y = startY;
    let s = 1;
    let done = 0;
    const paint = () => { ghost.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) scale(${s.toFixed(3)})`; ghost.style.opacity = String(Math.min(1, 0.25 + s)); };
    const finish = () => { done += 1; if (done === 3) { ghost.remove(); resolve(); } };
    const sx = Spring({ value: startX, response: 0.42, onUpdate: (v) => { x = v; paint(); }, onRest: finish });
    const sy = Spring({ value: startY, response: 0.42, onUpdate: (v) => { y = v; paint(); }, onRest: finish });
    const ss = Spring({ value: 1, response: 0.4, precision: 0.005, onUpdate: (v) => { s = v; paint(); }, onRest: finish });
    paint();
    sy.to(endY, { velocity: -240 }); // a small upward toss before it falls into the bag
    sx.to(endX);
    ss.to(0.12);
    window.setTimeout(() => { if (ghost.isConnected) { ghost.remove(); resolve(); } }, 1400);
  });

  /* ---------- Bag badge: a small spring "pop" when something is added ---------- */
  const popBadge = () => {
    if (!motionOK()) return;
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      const spring = Spring({ value: 1, response: 0.32, damping: 0.85, precision: 0.005, onUpdate: (s) => { badge.style.transform = `scale(${s.toFixed(3)})`; } });
      spring.to(1, { velocity: 3 });
    });
  };

  /* ---------- Statement: words light up as it crosses the viewport ---------- */
  document.querySelectorAll('[data-statement]').forEach((statement) => {
    const words = Array.from(statement.querySelectorAll('.w'));
    if (!words.length) return;
    if (reduceMotion.matches) { words.forEach((w) => w.classList.add('is-lit')); return; }
    let ticking = false;
    const update = () => {
      ticking = false;
      const rect = statement.getBoundingClientRect();
      const vh = window.innerHeight;
      // 0 when the block's top sits at 85% of the viewport, 1 when its bottom reaches 45%
      const total = rect.height + vh * 0.4;
      const progress = Math.min(1, Math.max(0, (vh * 0.85 - rect.top) / total));
      const lit = Math.round(progress * words.length);
      words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
    };
    const request = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    update();
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
  });

  /* ---------- Rails ---------- */
  document.querySelectorAll('[data-rail]').forEach((rail) => {
    const section = rail.closest('section');
    const prev = section?.querySelector('[data-rail-prev]');
    const next = section?.querySelector('[data-rail-next]');
    const sync = () => {
      const max = rail.scrollWidth - rail.clientWidth - 2;
      if (prev) prev.disabled = rail.scrollLeft <= 2;
      if (next) next.disabled = rail.scrollLeft >= max;
    };
    const step = () => {
      const item = rail.firstElementChild;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      return item ? (item.getBoundingClientRect().width + gap) * Math.max(1, Math.floor(rail.clientWidth / (item.getBoundingClientRect().width + gap))) : rail.clientWidth;
    };
    prev?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
    next?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
    rail.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });
    window.addEventListener('resize', sync);
    sync();
  });

  /* ---------- Quantity steppers (delegated so re-rendered drawers keep working) ---------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity] [data-quantity-action]');
    if (!button) return;
    const input = button.closest('[data-quantity]').querySelector('input');
    if (!input) return;
    const step = Number(input.step || 1);
    const min = Number(input.min || 0);
    const next = Number(input.value || 0) + (button.dataset.quantityAction === 'increase' ? step : -step);
    input.value = Math.max(min, next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Money ---------- */
  const formatMoney = (cents) => {
    const format = window.SMTheme?.moneyFormat || 'Rs.{{amount}}';
    const value = Number(cents) / 100;
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, token) => {
      if (token.includes('no_decimals') || Number.isInteger(value)) return Math.round(value).toLocaleString('en-US');
      if (token.includes('comma_separator')) return value.toFixed(2).replace('.', ',');
      return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    });
  };
  window.SMTheme = Object.assign(window.SMTheme || {}, { formatMoney });

  /* ---------- Cart ---------- */
  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
  };

  const renderCartDrawer = async (open = true) => {
    const drawer = document.querySelector('#CartDrawer');
    if (!drawer) return;
    const response = await fetch(`${window.SMTheme.routes.cart}?section_id=cart-drawer`);
    if (!response.ok) return;
    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    const fresh = parsed.querySelector('#CartDrawer');
    if (fresh) {
      const scrollTop = drawer.querySelector('.drawer__body')?.scrollTop || 0;
      const focused = drawer.contains(document.activeElement);
      drawer.innerHTML = fresh.innerHTML;
      const body = drawer.querySelector('.drawer__body');
      if (body) body.scrollTop = scrollTop;
      if (focused) drawer.querySelector('[data-drawer-close]')?.focus({ preventScroll: true });
      if (motionOK() && drawer.classList.contains('is-open')) {
        drawer.querySelectorAll('.cart-drawer__items, .sm-look--cart, .cart-summary__row').forEach(node => {
          node.animate([{ opacity: 0.45, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }], { duration: 220, easing: 'cubic-bezier(.22,1,.36,1)' });
        });
      }
    }
    const count = Number(drawer.querySelector('[data-cart-item-count]')?.dataset.cartItemCount || 0);
    updateCartCount(count);
    document.dispatchEvent(new CustomEvent('sm:cart-updated'));
    if (open) openDrawer(drawer, document.querySelector('[data-cart-trigger]'));
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    const giftSelected = Boolean(window.SMGiftBox?.selected(form));
    const useDrawer = Boolean(window.SMTheme?.cartDrawer && document.querySelector('#CartDrawer'));
    if (!useDrawer && !giftSelected) return;
    event.preventDefault();
    const submit = event.submitter?.type === 'submit' ? event.submitter : form.querySelector('[type="submit"]');
    const sourceForm = form.dataset.smGiftParent ? document.getElementById(form.dataset.smGiftParent) || form : form;
    const errorBox = sourceForm.querySelector('[data-product-error]');
    if (form.dataset.smSubmitting === 'true') return;
    form.dataset.smSubmitting = 'true';
    const wasDisabled = submit?.disabled;
    if (submit) submit.disabled = true;
    if (errorBox) errorBox.hidden = true;
    submit?.setAttribute('aria-busy', 'true');
    try {
      const body = window.SMGiftBox?.build(form) || new FormData(form);
      const response = await fetch(window.SMTheme.routes.cartAdd, { method: 'POST', headers: { Accept: 'application/json' }, body });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'This item could not be added to your bag.');
      if (event.submitter?.hasAttribute('data-sm-gift-checkout')) {
        window.location.assign(window.SMTheme.routes.root + 'checkout');
        return;
      }
      if (!useDrawer || document.body.classList.contains('template-cart')) { window.location.assign(window.SMTheme.routes.cart); return; }
      // The photo arcs into the bag while the drawer content loads, then the bag opens
      void flyToBag(form);
      await renderCartDrawer(false);
      popBadge();
      openDrawer(document.querySelector('#CartDrawer'), document.querySelector('[data-cart-trigger]'));
      announce('Added to your bag.');
    } catch (error) {
      announce(error.message);
      if (errorBox) { errorBox.hidden = false; errorBox.textContent = error.message; }
    } finally {
      submit?.removeAttribute('aria-busy');
      if (submit) submit.disabled = Boolean(wasDisabled);
      delete form.dataset.smSubmitting;
    }
  });

  document.addEventListener('change', async (event) => {
    const cartInput = event.target.closest('[data-cart-quantity]');
    if (!cartInput) return;
    cartInput.closest('.cart-item')?.setAttribute('aria-busy', 'true');
    try {
      await fetch(window.SMTheme.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(cartInput.dataset.line), quantity: Number(cartInput.value) })
      });
      if (cartInput.closest('#CartDrawer')) await renderCartDrawer(false);
      else window.location.reload();
    } catch (error) {
      announce('Your bag could not be updated. Refresh the page and try again.');
    }
  });

  /* ---------- Predictive search ---------- */
  let searchTimer;
  document.querySelectorAll('[data-predictive-input]').forEach((input) => input.addEventListener('input', () => {
    window.clearTimeout(searchTimer);
    const target = document.querySelector(input.dataset.predictiveInput);
    if (!target) return;
    const query = input.value.trim();
    if (query.length < 2) { target.innerHTML = ''; return; }
    searchTimer = window.setTimeout(async () => {
      target.setAttribute('aria-busy', 'true');
      try {
        const url = `${window.SMTheme.routes.predictiveSearch}?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=6&section_id=predictive-search`;
        const response = await fetch(url);
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        target.innerHTML = parsed.querySelector('[data-predictive-results]')?.innerHTML || '';
      } finally { target.removeAttribute('aria-busy'); }
    }, 220);
  }));

  /* ---------- Collection filters & misc ---------- */
  document.querySelectorAll('[data-filter-form] select').forEach((select) => select.addEventListener('change', () => select.form.submit()));
  document.querySelectorAll('[data-comparison]').forEach((comparison) => {
    const input = comparison.querySelector('input');
    input?.addEventListener('input', () => comparison.style.setProperty('--position', `${input.value}%`));
  });

  /* ---------- Recommendations loaded after the page ---------- */
  document.querySelectorAll('[data-recommendations-url]').forEach(async (container) => {
    try {
      const response = await fetch(container.dataset.recommendationsUrl);
      if (!response.ok) return;
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = parsed.querySelector('[data-recommendations-url]');
      if (fresh?.innerHTML.trim()) { container.innerHTML = fresh.innerHTML; observeReveals(container); }
    } catch (error) { /* keep the server-rendered fallback */ }
  });

  root.classList.add('js-ready');
})();



